import { useEffect, useRef, useCallback } from 'react';
import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { S3MultipartSigner } from './S3MultipartSigner';
import { UploadApi } from './UploadApi';
import { UploadUtils } from './UploadUtils';
import { useUploadStore } from './uploadStore';
import { ensureValidCredentials } from './useUploadCredentials';
import { MEDIA_QUERY_KEYS } from '@/lib/constants/media';
import {
  VIDEO_EXTENSIONS,
  MAX_UPLOAD_SIZE_BYTES,
  UPLOAD_REQUEST_CONCURRENCY,
  MULTIPART_MIN_PART_SIZE,
  UPLOAD_RETRY_DELAYS,
  PROGRESS_REPORT_THROTTLE_MS,
} from '@/lib/constants/upload';
import type { IngestcfgData } from '@/lib/types/Ingest';

/**
 * Core hook: manages Uppy instance with @uppy/aws-s3 multipart plugin.
 *
 * Wires all Uppy callbacks to Zustand store, S3MultipartSigner, and UploadApi.
 * Uppy instance is stored in useRef — NOT in Zustand (avoids serialization issues).
 *
 * Lifecycle: recreated ONLY when selectedCId changes (cleanup cancels all +
 * destroys). Nothing else may enter the recreate deps: recreating tears down
 * in-flight S3 uploads.
 */
export function useUppy(
  cId: number | null,
  ingestcfgs: IngestcfgData[],
  selectedIngestcfgId: number | null
) {
  const uppyRef = useRef<Uppy | null>(null);
  const queryClient = useQueryClient();

  // Per-file progress report throttle timestamps (in hook scope, not Zustand)
  const lastReportTSRef = useRef<Map<string, number>>(new Map());

  // Latest profiles/selection, read inside Uppy callbacks via refs so they
  // stay out of the createUppyInstance deps — with the profile in the deps,
  // changing the selector mid-upload recreated the instance and the cleanup
  // (cancelAll + destroy) aborted the in-flight S3 upload.
  const ingestcfgsRef = useRef(ingestcfgs);
  ingestcfgsRef.current = ingestcfgs;
  const selectedIngestcfgIdRef = useRef(selectedIngestcfgId);
  selectedIngestcfgIdRef.current = selectedIngestcfgId;

  const createUppyInstance = useCallback(() => {
    if (cId == null) return null;

    // Capture cId for closures — prevents stale reference if cId changes
    const capturedCId = cId;

    const uppy = new Uppy({
      id: 'video-upload',
      autoProceed: true,
      restrictions: {
        allowedFileTypes: [...VIDEO_EXTENSIONS],
        maxFileSize: MAX_UPLOAD_SIZE_BYTES,
      },
    });

    uppy.use(AwsS3, {
      shouldUseMultipart: true as const,
      getChunkSize: (file) =>
        Math.max(MULTIPART_MIN_PART_SIZE, Math.ceil(file.size / 10_000)),
      limit: UPLOAD_REQUEST_CONCURRENCY,
      retryDelays: [...UPLOAD_RETRY_DELAYS],

      // --- Adapter layer: Uppy callbacks → S3MultipartSigner + UploadApi ---

      async createMultipartUpload(file) {
        const credentials = await ensureValidCredentials(capturedCId);

        // Find the UploadItem by uploadItemId meta or Uppy file ID
        const { items } = useUploadStore.getState();
        const uploadItemId = file.meta?.uploadItemId as string | undefined;
        const item = uploadItemId
          ? items.find((i) => i.id === uploadItemId)
          : items.find((i) => i.uppyFileId === file.id);

        if (!item) {
          throw new Error(`No UploadItem found for Uppy file ${file.id}`);
        }

        // Resolve the profile the file was queued under (items snapshot
        // their ingestcfgId at queue time), so switching the selector
        // mid-batch never changes files already queued. Falls back to the
        // current selection for items queued before profiles loaded.
        const cfgs = ingestcfgsRef.current;
        const currentIngestcfg =
          cfgs.find((c) => c.ingestcfgId === item.ingestcfgId) ??
          cfgs.find((c) => c.ingestcfgId === selectedIngestcfgIdRef.current) ??
          null;
        if (!currentIngestcfg) {
          throw new Error('Ingest config not loaded');
        }

        // Re-entry after a create-phase failure (retry) must NEVER register a
        // second media row for the same picked file — before this guard, each
        // retry of a failed registration minted another phantom AwaitingIngest
        // row. (Part-level retries never re-enter here: Uppy caches the
        // uploadId per file and resumes via listParts.)
        let key = item.s3Key;
        if (!(item.mediaId > 0 && key)) {
          const req = UploadUtils.buildCreateVideoRequest(item, currentIngestcfg);

          // Create backend media record — get S3 key, mediaId, ingestJobId
          const result = await UploadApi.createVideo(capturedCId, req);

          if (!result.dashboard) {
            throw new Error('Backend returned no dashboard ingest data');
          }

          const { key: newKey, ingestJobId, data } = result.dashboard;
          const mediaId = data.video?.media.mediaId ?? 0;
          key = newKey;

          // Update Zustand item with backend response
          useUploadStore.getState().updateItem(item.id, {
            mediaId,
            ingestJobId,
            s3Key: newKey,
            status: 'creating',
          });
        } else {
          useUploadStore.getState().updateItem(item.id, { status: 'creating' });
        }

        // Initiate S3 multipart upload. Hold the uploadId on the item: Uppy
        // aborts and forgets its own multipart state on terminal failure, so
        // this is the only durable handle for part-level resume on retry.
        const contentType = UploadUtils.getContentType(item.filename);
        const multipart = await S3MultipartSigner.createMultipartUpload(
          credentials,
          key,
          contentType
        );
        useUploadStore.getState().updateItem(item.id, { uploadId: multipart.uploadId });

        return { uploadId: multipart.uploadId, key: multipart.key };
      },

      async signPart(file, opts) {
        // CRITICAL for long uploads: credentials can expire mid-upload
        const credentials = await ensureValidCredentials(capturedCId);
        return S3MultipartSigner.signPart(
          credentials,
          opts.key,
          opts.uploadId,
          opts.partNumber
        );
      },

      async listParts(file, opts) {
        const { credentials } = useUploadStore.getState();
        if (!credentials) throw new Error('No credentials available for listParts');
        if (!opts.uploadId) throw new Error('No uploadId for listParts');
        return S3MultipartSigner.listParts(credentials, opts.key, opts.uploadId);
      },

      async completeMultipartUpload(file, opts) {
        const { credentials } = useUploadStore.getState();
        if (!credentials) throw new Error('No credentials available for completeMultipartUpload');
        if (!opts.uploadId) throw new Error('No uploadId for completeMultipartUpload');

        // Map AwsS3Part (optional fields) → required fields for our signer
        const parts = opts.parts
          .filter((p): p is Required<typeof p> => p.ETag != null && p.PartNumber != null)
          .map((p) => ({ ETag: p.ETag!, PartNumber: p.PartNumber! }));

        return S3MultipartSigner.completeMultipartUpload(
          credentials,
          opts.key,
          opts.uploadId,
          parts
        );
      },

      async abortMultipartUpload(file, opts) {
        if (!opts.uploadId) return; // Nothing to abort without an uploadId

        // Uppy routes TERMINAL FAILURES through here too, not just cancels.
        // Keep the parts on failure — they are what retry resumes from; the
        // bucket lifecycle aborts incomplete uploads after 4 days regardless.
        // Only a user cancel (status 'cancelled', set before removeFile) or a
        // teardown with no item left (account switch reset) truly aborts.
        const uploadItemId = file.meta?.uploadItemId as string | undefined;
        const { items } = useUploadStore.getState();
        const item = uploadItemId
          ? items.find((i) => i.id === uploadItemId)
          : items.find((i) => i.uppyFileId === file.id);
        if (item && item.status !== 'cancelled') return;

        // Credentials may be null during customer switch — null-guard in S3MultipartSigner
        const { credentials } = useUploadStore.getState();
        await S3MultipartSigner.abortMultipartUpload(
          credentials,
          opts.key,
          opts.uploadId
        );
      },
    });

    // --- Wire Uppy events → Zustand store ---

    uppy.on('upload-progress', (file, progress) => {
      if (!file) return;

      const uploadItemId = file.meta?.uploadItemId as string | undefined;
      const { items } = useUploadStore.getState();
      const item = uploadItemId
        ? items.find((i) => i.id === uploadItemId)
        : items.find((i) => i.uppyFileId === file.id);

      if (!item) return;

      const bytesUploaded = progress.bytesUploaded;
      const bytesTotal = progress.bytesTotal ?? item.bytesTotal;
      // Capped at 99 until upload-success confirms the S3 complete —
      // UploadUtils.transferProgress doc explains why (close-at-100 losses).
      const uploadProgress = UploadUtils.transferProgress(bytesUploaded, bytesTotal);

      useUploadStore.getState().updateItem(item.id, {
        bytesUploaded,
        bytesTotal,
        uploadProgress,
        status: 'uploading',
        startedAt: item.startedAt ?? Date.now(),
        lastProgressAt: Date.now(),
      });

      // ON FIRST PROGRESS: notify backend that media is ingesting
      if (!item.ingestingNotified && item.mediaId > 0) {
        useUploadStore.getState().updateItem(item.id, { ingestingNotified: true });
        UploadApi.updateStatusToIngesting(capturedCId, item.mediaId).catch(() => {
          // Non-critical — log and continue
        });
      }

      // Throttled progress report to backend — same 99 cap, so a stored
      // completed_rate of 100 always means the S3 complete was confirmed.
      if (item.ingestJobId > 0 && bytesTotal > 0) {
        const lastTS = lastReportTSRef.current.get(item.id) ?? 0;
        if (Date.now() - lastTS > PROGRESS_REPORT_THROTTLE_MS) {
          lastReportTSRef.current.set(item.id, Date.now());
          UploadApi.updateProgress(capturedCId, item.ingestJobId, uploadProgress).catch(() => {
            // Non-critical — log and continue
          });
        }
      }
    });

    uppy.on('upload-success', (file) => {
      if (!file) return;

      const uploadItemId = file.meta?.uploadItemId as string | undefined;
      const { items } = useUploadStore.getState();
      const item = uploadItemId
        ? items.find((i) => i.id === uploadItemId)
        : items.find((i) => i.uppyFileId === file.id);

      if (!item) return;

      // Stay in 'uploading' — the item flips to 'processing' only when the
      // backend confirms the ingest job completed (useJobPolling), the same
      // authority the videos grid reads (mediaStatusType). Flipping here on
      // the client S3 event made the drawer say "Processing" while the grid
      // still said "Uploading" for the same file. lastProgressAt restarts
      // the stall clock for the handoff wait.
      useUploadStore.getState().updateItem(item.id, {
        uploadProgress: 100,
        lastProgressAt: Date.now(),
      });

      // The multipart completed — the held resume handle is spent.
      useUploadStore.getState().updateItem(item.id, { uploadId: null });

      // Final 100% progress report — the ONLY place 100 is ever reported,
      // so completed_rate=100 in the DB means the S3 complete was confirmed.
      if (item.ingestJobId > 0) {
        UploadApi.updateProgress(capturedCId, item.ingestJobId, 100).catch(() => {});
      }

      // Invalidate video list to show new video
      queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEYS.videos(capturedCId) });
    });

    uppy.on('upload-error', (file, error) => {
      if (!file) return;

      const uploadItemId = file.meta?.uploadItemId as string | undefined;
      const { items } = useUploadStore.getState();
      const item = uploadItemId
        ? items.find((i) => i.id === uploadItemId)
        : items.find((i) => i.uppyFileId === file.id);

      if (!item) return;

      // 'upload-error' is TERMINAL for the file (Uppy's per-part retryDelays
      // are already exhausted). Swallowing it when bytes had been sent left
      // files stuck "uploading" until the 5-min stale sweep — on phones the
      // tab was long dead by then and the failure was never seen (2026-07
      // shopper incident). Job polling still promotes the item if the S3
      // object somehow landed; retry stays available on the row.
      useUploadStore.getState().updateItem(item.id, {
        status: 'error',
        error: error?.message ?? 'Upload failed',
      });
    });

    uppy.on('restriction-failed', (_file, error) => {
      notifications.show({
        title: 'File rejected',
        message: error?.message ?? 'File does not meet upload requirements',
        color: 'red',
      });
    });

    return uppy;
  }, [cId, queryClient]);

  // Lifecycle: recreate Uppy when cId changes
  useEffect(() => {
    const instance = createUppyInstance();
    uppyRef.current = instance;
    lastReportTSRef.current.clear();

    return () => {
      if (instance) {
        instance.cancelAll();
        instance.destroy();
      }
    };
  }, [createUppyInstance]);

  /**
   * Retry a failed item, resuming from the parts that already reached S3
   * when possible. Uppy ABORTS and forgets its multipart state on terminal
   * failure (verified against @uppy/aws-s3 5.1 behaviorally) — our abort
   * adapter skips the S3 abort for failures so the parts survive, and the
   * uploadId held on the item re-seeds file.s3Multipart here, which routes
   * Uppy through its restore path: listParts, upload only missing parts, no
   * createMultipartUpload re-call. The handle is one-shot: consumed by this
   * retry, so a failed resume degrades to a clean fresh upload (with the
   * media-row registration still reused via the adapter's idempotence guard).
   */
  const retryItem = useCallback(
    (item: { id: string; uppyFileId: string; s3Key: string; uploadId: string | null }) => {
      const uppy = uppyRef.current;
      if (!uppy || !item.uppyFileId) return;
      // 'pending' until the first progress event; reset the stall clock so the
      // stale sweep doesn't judge the resume by the failure's timestamps.
      useUploadStore.getState().updateItem(item.id, {
        status: 'pending',
        error: null,
        lastProgressAt: Date.now(),
      });
      try {
        if (item.uploadId && item.s3Key && uppy.getFile(item.uppyFileId)) {
          // s3Multipart is plugin-runtime file state — @uppy/aws-s3 reads it
          // in its restore path but doesn't augment UppyFile's type.
          uppy.setFileState(item.uppyFileId, {
            s3Multipart: { key: item.s3Key, uploadId: item.uploadId },
          } as unknown as Parameters<typeof uppy.setFileState>[1]);
          useUploadStore.getState().updateItem(item.id, { uploadId: null });
        }
        // Upload failures surface via the upload-error handler; the returned
        // promise duplicates them, so swallow its rejection.
        uppy.retryUpload(item.uppyFileId).catch(() => {});
      } catch {
        // Uppy no longer knows this file (sync throw) — nothing to resume.
        useUploadStore.getState().updateItem(item.id, {
          status: 'error',
          error: 'Could not resume — use Clear completed, then add the file again',
        });
      }
    },
    []
  );

  const addFilesWithMeta = useCallback(
    (filePairs: Array<{ file: File; uploadItemId: string }>) => {
      const uppy = uppyRef.current;
      if (!uppy) return;

      for (const { file, uploadItemId } of filePairs) {
        // A re-picked file that is already in the queue (Uppy's duplicate
        // fingerprint: name+size+mtime) must not mint a fresh registration.
        // If the queued copy failed, resume it from its uploaded parts —
        // shoppers re-add failed files far more often than they hit Retry,
        // and each re-add used to restart from byte 0 under a new media row.
        const existing = UploadUtils.findQueuedMatch(
          useUploadStore.getState().items,
          file
        );
        if (existing) {
          useUploadStore.getState().removeItem(uploadItemId);
          if (existing.status === 'error') {
            retryItem(existing);
            notifications.show({
              title: 'Resuming upload',
              message: `${file.name} continues from where it left off`,
              color: 'blue',
            });
          } else {
            notifications.show({
              title: 'Already in queue',
              message: `${file.name} is already in the upload queue`,
              color: 'orange',
            });
          }
          continue;
        }

        try {
          const uppyFileId = uppy.addFile({
            name: file.name,
            type: file.type || UploadUtils.getContentType(file.name),
            data: file,
            meta: { uploadItemId },
          });

          // Backlink Uppy file ID to UploadItem
          useUploadStore.getState().updateItem(uploadItemId, { uppyFileId });
        } catch {
          // Backstop for Uppy-side duplicate rejection when no store item
          // matched (e.g. the queue row was cleared but the file is still in
          // Uppy state) — restriction-failed shows the notification. Remove
          // the store item: left in place it stays 'pending' forever
          // (uppyFileId never set), pinning the queue at "Sending…".
          useUploadStore.getState().removeItem(uploadItemId);
        }
      }
    },
    [retryItem]
  );

  const cancelUpload = useCallback((uppyFileId: string) => {
    const uppy = uppyRef.current;
    if (!uppy || !uppyFileId) return;
    // Status FIRST: the abort adapter distinguishes a user cancel (really
    // abort the S3 multipart) from a failure-path abort (keep the parts for
    // resume) by reading it. removeFile also fires no upload-error, so the
    // item would otherwise sit 'uploading' until the stale sweep.
    useUploadStore.getState().updateItemByUppyId(uppyFileId, { status: 'cancelled' });
    uppy.removeFile(uppyFileId);
  }, []);

  const cancelAll = useCallback(() => {
    const uppy = uppyRef.current;
    if (!uppy) return;
    uppy.cancelAll();
  }, []);

  const retryUpload = useCallback(
    (uppyFileId: string) => {
      if (!uppyFileId) return;
      const item = useUploadStore.getState().items.find((i) => i.uppyFileId === uppyFileId);
      if (item) retryItem(item);
    },
    [retryItem]
  );

  const retryAllFailed = useCallback(() => {
    const { items } = useUploadStore.getState();
    for (const item of items) {
      if (item.status === 'error' && item.uppyFileId) retryItem(item);
    }
  }, [retryItem]);

  /**
   * Clear terminal rows AND release their files from Uppy state — otherwise
   * cleared files stay fingerprinted (blocking an intentional re-upload until
   * reload) and their Blobs stay pinned in memory for the session.
   */
  const clearCompleted = useCallback(() => {
    const uppy = uppyRef.current;
    const { items } = useUploadStore.getState();
    if (uppy) {
      for (const item of items) {
        if (
          (item.status === 'ready' || item.status === 'error' || item.status === 'cancelled') &&
          item.uppyFileId &&
          uppy.getFile(item.uppyFileId)
        ) {
          uppy.removeFile(item.uppyFileId);
        }
      }
    }
    useUploadStore.getState().clearCompleted();
  }, []);

  return { addFilesWithMeta, cancelUpload, cancelAll, retryUpload, retryAllFailed, clearCompleted };
}
